import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Tooltip } from '@mui/material';
import { EventRepeat, Payments, Visibility } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { bookingStudioOf } from '../../utils/studioOf';
import { isOwnerRole } from '../../utils/roles';
import RescheduleBookingDialog from '../RescheduleBookingDialog';
import type { DrawerBooking } from './SessionBookingsDrawer';
import { isPaymentPending } from './bookingRowBits';
import { SCHED, btnPrimarySx, btnSx } from './scheduleTheme';

interface BookingQuickActionsProps {
  booking: DrawerBooking;
  /** The booking moved or was paid: the list refetches. */
  onChanged: () => void;
  onError: (message: string) => void;
}

const iconSx = {
  p: 0.75,
  flexShrink: 0,
  color: SCHED.muted,
  '&:hover': { backgroundColor: SCHED.hover, color: SCHED.ink },
};

/**
 * Same actions and rules as the Bookings page's row menu. Reschedule and Mark
 * paid are super admin / studio owner only (the server rejects anyone else). A
 * pack booking's payment is settled on the pack, so it has no Mark paid here.
 */
const BookingQuickActions: React.FC<BookingQuickActionsProps> = ({ booking, onChanged, onError }) => {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [rescheduling, setRescheduling] = useState(false);
  const [confirmingPaid, setConfirmingPaid] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);

  const canManage = admin?.role === 'super_admin' || admin?.role === 'admin' || isOwnerRole(admin?.role);
  const active = booking.bookingStatus !== 'CANCELLED';
  const canMarkPaid =
    canManage && booking.transaction?.modeOfPayment !== 'subscription' && isPaymentPending(booking);
  const amount = (booking.transaction?.basePrice ?? 0) + (booking.transaction?.taxes ?? 0);

  const markPaid = async () => {
    setMarkingPaid(true);
    try {
      await apiService.markBookingPaid(booking.bookingId || booking._id, bookingStudioOf(booking));
      setConfirmingPaid(false);
      onChanged();
    } catch (err: any) {
      setConfirmingPaid(false);
      onError(err?.response?.data?.message || err?.message || 'Failed to mark as paid');
    } finally {
      setMarkingPaid(false);
    }
  };

  return (
    <>
      {canMarkPaid && (
        <Tooltip title="Mark paid">
          <IconButton
            size="small"
            aria-label="Mark paid"
            onClick={() => setConfirmingPaid(true)}
            sx={{ ...iconSx, color: SCHED.waitText, '&:hover': { backgroundColor: SCHED.waitBg, color: SCHED.waitText } }}
          >
            <Payments sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
      {canManage && active && (
        <Tooltip title="Reschedule">
          <IconButton size="small" aria-label="Reschedule" onClick={() => setRescheduling(true)} sx={iconSx}>
            <EventRepeat sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="View booking">
        <IconButton size="small" aria-label="View booking" onClick={() => navigate(`/bookings/${booking._id}`)} sx={iconSx}>
          <Visibility sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>

      {rescheduling && (
        <RescheduleBookingDialog
          open
          booking={booking}
          onClose={() => setRescheduling(false)}
          onSuccess={() => {
            setRescheduling(false);
            onChanged();
          }}
        />
      )}

      <Dialog open={confirmingPaid} onClose={() => !markingPaid && setConfirmingPaid(false)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 3, fontFamily: SCHED.font }}>
          <Box sx={{ fontSize: '15px', fontWeight: 600, mb: '12px' }}>{booking.user?.name || 'Guest'}</Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '10px 12px', borderRadius: '12px', backgroundColor: SCHED.hover }}>
            <Box component="span" sx={{ fontSize: '14px', color: SCHED.muted }}>Collected at studio</Box>
            <Box component="span" sx={{ fontSize: '16px', fontWeight: 700 }}>₹{amount.toLocaleString('en-IN')}</Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: '0 24px 20px', gap: '8px' }}>
          <Button onClick={() => setConfirmingPaid(false)} disabled={markingPaid} sx={btnSx}>Back</Button>
          <Button onClick={markPaid} disabled={markingPaid} sx={btnPrimarySx}>
            {markingPaid ? 'Marking...' : 'Mark as Paid'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BookingQuickActions;
